import {
  addHours,
  differenceInCalendarDays,
  differenceInHours,
  isAfter,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { useCallback, useMemo, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { paymentApi } from '$api-clients';
import { usePayment } from '$api-clients/hooks';
import { useAppFlags } from '$launch-darkly/app-flags';
import { type PlatformFee, PlatformFeeAlertStatus } from './types';

const POLL_INTERVAL_MS = 60_000;

const PAST_DUE_ALERT_GRACE_PERIOD_HOURS = 24;
const PAST_DUE_TAKEOVER_GRACE_PERIOD_DAYS = 7;

function getPlatformFeeAlertStatus(
  platformFee: PlatformFee,
  featureFlag?: boolean,
  hasLeadTeamMember?: boolean,
): PlatformFeeAlertStatus | null {
  if (!platformFee) {
    return null;
  }

  const {
    pastDueBalance,
    activePlatformFeesPaying,
    hasDefaultPaymentMethod,
    hasValidDefaultPaymentMethod,
    oldestPastDueInvoiceDueDate,
    unpaidPastDueInvoices,
  } = platformFee;
  const isPayor = activePlatformFeesPaying && activePlatformFeesPaying > 0;
  const hasPastDueBalance = pastDueBalance && pastDueBalance > 0;
  const isPastDueForMoreThanOneDay =
    oldestPastDueInvoiceDueDate &&
    differenceInHours(
      new Date(),
      fromZonedTime(oldestPastDueInvoiceDueDate, 'America/Los_Angeles'),
    ) > PAST_DUE_ALERT_GRACE_PERIOD_HOURS;

  const isPastDueForMoreThanOneWeek =
    oldestPastDueInvoiceDueDate &&
    differenceInCalendarDays(
      new Date(),
      fromZonedTime(oldestPastDueInvoiceDueDate, 'America/Los_Angeles'),
    ) > PAST_DUE_TAKEOVER_GRACE_PERIOD_DAYS;

  // Banner priorities: PLATFORM_FEE_ENFORCEMENT > MULTIPLE_PAST_DUE_INVOICES > MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE > MISSING_PAYMENT_METHOD > INVALID_PAYMENT_METHOD > PAST_DUE_BALANCE
  // PLATFORM_FEE_ENFORCEMENT and MULTIPLE_PAST_DUE_INVOICES are shown only to Associates (!hasLeadTeamMember). Partners have AGENT and LEAD role; Associates have AGENT only.
  if (
    !hasLeadTeamMember &&
    featureFlag &&
    (unpaidPastDueInvoices ?? 0) >= 3 &&
    isPastDueForMoreThanOneWeek
  ) {
    return PlatformFeeAlertStatus.PLATFORM_FEE_ENFORCEMENT;
  }

  if (
    !hasLeadTeamMember &&
    (unpaidPastDueInvoices ?? 0) >= 2 &&
    isPastDueForMoreThanOneWeek
  ) {
    return PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES;
  }

  if (
    hasPastDueBalance &&
    !hasDefaultPaymentMethod &&
    isPastDueForMoreThanOneDay
  ) {
    return PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE;
  }

  if (isPayor && !hasDefaultPaymentMethod) {
    return PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD;
  }

  if (
    hasPastDueBalance &&
    hasDefaultPaymentMethod &&
    hasValidDefaultPaymentMethod &&
    isPastDueForMoreThanOneDay
  ) {
    return PlatformFeeAlertStatus.PAST_DUE_BALANCE;
  }

  if (isPayor && !hasValidDefaultPaymentMethod) {
    return PlatformFeeAlertStatus.INVALID_PAYMENT_METHOD;
  }

  return null;
}

export { getPlatformFeeAlertStatus };

export function getPlatformFeeRefreshInterval({
  platformFee,
  featureFlag,
  hasLeadTeamMember,
  isIdle,
}: {
  platformFee: PlatformFee;
  featureFlag?: boolean;
  hasLeadTeamMember?: boolean;
  isIdle: boolean;
}): number {
  const status = getPlatformFeeAlertStatus(
    platformFee,
    featureFlag,
    hasLeadTeamMember,
  );

  return status && !isIdle ? POLL_INTERVAL_MS : 0;
}

export function useFetchPlatformFee(hasLeadTeamMember?: boolean) {
  const { paymentsPlatformFeeEnforcement } = useAppFlags();
  const [isIdle, setIsIdle] = useState(false);

  const refreshInterval = useCallback(
    (latestData?: PlatformFee) =>
      getPlatformFeeRefreshInterval({
        platformFee: latestData ?? null,
        featureFlag: paymentsPlatformFeeEnforcement,
        hasLeadTeamMember,
        isIdle,
      }),
    [paymentsPlatformFeeEnforcement, hasLeadTeamMember, isIdle],
  );

  const { data } = usePayment('/me/platform-fee', {}, { refreshInterval });

  useIdleTimer({
    onIdle: () => setIsIdle(true),
    onActive: () => setIsIdle(false),
  });

  const status = useMemo(
    () =>
      getPlatformFeeAlertStatus(
        data ?? null,
        paymentsPlatformFeeEnforcement,
        hasLeadTeamMember,
      ),
    [data, paymentsPlatformFeeEnforcement, hasLeadTeamMember],
  );

  const {
    oldestPastDueInvoiceDueDate = null,
    pastDueBalance = null,
    unpaidPastDueInvoices = 0,
  } = data ?? {};

  return {
    status,
    oldestPastDueInvoiceDueDate,
    pastDueBalance,
    unpaidPastDueInvoices,
  };
}

export async function getPaymentCenterUrl(
  status: PlatformFeeAlertStatus | null,
  redirectUrl?: string,
) {
  const { data } = await paymentApi.GET('/me/payment-portal', {
    params: {
      query: {
        returnUrl: redirectUrl ?? window.location.href,
        flow:
          status !== PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD
            ? 'PORTAL'
            : 'UPDATE_PAYMENT_METHOD',
      },
    },
  });

  return data?.url;
}

const DISMISSAL_DURATION_HOURS = 24;

const TAKEOVER_CONTENTS: Partial<
  Record<
    PlatformFeeAlertStatus,
    {
      title: string;
      description: string;
      linkText: string;
    }
  >
> = {
  [PlatformFeeAlertStatus.INVALID_PAYMENT_METHOD]: {
    title: 'Past Due Platform Fee Payment',
    description:
      'The payment method on file is invalid. Update it and pay now to resolve your past due balance and stay up to date with monthly payments.',
    linkText: 'Update Payment Method & Pay',
  },
  [PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD]: {
    title: 'Past Due Platform Fee Payment',
    description:
      'Add a payment method to resolve your past due balance, and to stay up to date with the monthly payments',
    linkText: 'Add Payment Method & Pay',
  },
  [PlatformFeeAlertStatus.PAST_DUE_BALANCE]: {
    title: 'Past Due Platform Fee Payment',
    description: 'You have a past due balance, resolve it by paying now',
    linkText: 'Pay now',
  },
  [PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE]: {
    title: 'Past Due Platform Fee Payment',
    description:
      'Add a payment method to resolve your balance, and to stay up to date with the monthly payments',
    linkText: 'Add & Pay',
  },
  [PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES]: {
    title: 'Multiple Past Due Invoices',
    description:
      'Your account has a past due balance which can be resolved by paying now',
    linkText: 'Pay Now',
  },
  [PlatformFeeAlertStatus.PLATFORM_FEE_ENFORCEMENT]: {
    title: 'Multiple Past Due Invoices',
    description:
      'Your account has a past due balance which can be resolved by paying now.',
    linkText: 'Pay Now',
  },
};

const DISMISSAL_KEY = `platform-fee-alert-takeover-dismissed-at`;

/** When true, PLATFORM_FEE_ENFORCEMENT and MULTIPLE_PAST_DUE_INVOICES takeovers are hidden (associates only see them). */
export function usePlatformFeeTakeover({
  status,
  oldestPastDueInvoiceDueDate,
  pastDueBalance,
  hasLeadTeamMember = false,
}: {
  status: PlatformFeeAlertStatus | null;
  oldestPastDueInvoiceDueDate: string | null;
  pastDueBalance: number | null;
  hasLeadTeamMember?: boolean;
}) {
  const dismissedTakeoverAt = JSON.parse(
    localStorage.getItem(DISMISSAL_KEY) ?? 'null',
  ) as number | null;
  const setDismissedTakeoverAt = (value: number | null) => {
    localStorage.setItem(DISMISSAL_KEY, JSON.stringify(value));
  };
  const isEnforcementTakeoverHiddenForLead =
    !!hasLeadTeamMember &&
    (status === PlatformFeeAlertStatus.PLATFORM_FEE_ENFORCEMENT ||
      status === PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES);
  const content =
    status && !isEnforcementTakeoverHiddenForLead
      ? TAKEOVER_CONTENTS[status]
      : null;
  const hasBeenDismissed =
    !!dismissedTakeoverAt &&
    !isAfter(
      new Date(),
      addHours(new Date(dismissedTakeoverAt), DISMISSAL_DURATION_HOURS),
    );

  const shouldShowMissingPaymentMethodTakeover =
    status ===
      PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE &&
    !!pastDueBalance &&
    pastDueBalance > 0;

  const shouldShowPastDueBalanceTakeover =
    status === PlatformFeeAlertStatus.PAST_DUE_BALANCE &&
    !!oldestPastDueInvoiceDueDate &&
    differenceInCalendarDays(
      new Date(),
      fromZonedTime(oldestPastDueInvoiceDueDate, 'America/Los_Angeles'),
    ) > PAST_DUE_TAKEOVER_GRACE_PERIOD_DAYS;

  const shouldShowInvalidPaymentMethodTakeover =
    status === PlatformFeeAlertStatus.INVALID_PAYMENT_METHOD;

  const shouldShowMultiplePastDueInvoicesTakeover =
    !hasLeadTeamMember &&
    status === PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES;

  const shouldShowPlatformFeeEnforcementTakeover =
    !hasLeadTeamMember &&
    status === PlatformFeeAlertStatus.PLATFORM_FEE_ENFORCEMENT;

  const shouldShowTakeover =
    !!status &&
    (shouldShowPlatformFeeEnforcementTakeover ||
      (!hasBeenDismissed &&
        (shouldShowMissingPaymentMethodTakeover ||
          shouldShowInvalidPaymentMethodTakeover ||
          shouldShowPastDueBalanceTakeover ||
          shouldShowMultiplePastDueInvoicesTakeover)));

  return {
    shouldShowTakeover,
    content,
    setDismissedTakeoverAt,
  };
}
